import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Group,
  Text,
  Badge,
  Button,
  Stack,
  UnstyledButton,
  Tooltip,
} from "@mantine/core";
import {
  IconPrinter,
  IconChevronDown,
  IconChevronUp,
  IconCategory,
  IconLabel,
  IconStack2,
  IconLayersIntersect,
  IconScissors,
  IconEdit,
} from "@tabler/icons-react";
import { isEmptyHtml } from "../../../utils/helpers";
import { getFragmentCountDisplayValue } from "../../../utils/fragmentCount";
import { getPhysicalSlideNumber } from "../../../utils/caseIdentity";
import { CptCorrectionModal } from "./CptCorrectionModal";
import { SlidePill } from "./SlidePill";
import {
  PrintStickerButton,
  ReceivedExternalBadge,
} from "../../../components/sticker";

interface Props {
  caseId: string;
  specimens: any[];
  showTechnicalPrintActions: boolean;
  onPrintAllCassettes: () => void;
  onPrintAllSlides: () => void;
  onPrintSpecimenCassettes: (specimenId: string) => void;
  onPrintSpecimenSlides: (specimenId: string) => void;
  printCassetteLoading: boolean;
  printSlideLoading: boolean;
  isFinalized?: boolean;
  isBlocked?: boolean;
  caseNumber?: string;
}

const MATERIAL_LABELS: Record<string, string> = {
  formalin_jar: "Formalin jar",
  block: "Block",
  slides_only: "Slides only",
  blood_tube: "Blood tube",
  urine_sample: "Urine sample",
  stool_collection: "Stool collection",
};

function getMaterialLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return MATERIAL_LABELS[value] || value;
}

function resolveGrossVariables(html: string): string {
  if (!html) return "";
  return html
    .replace(
      /<span[^>]*data-variable-value="([^"]*)"[^>]*>\{\{\w+\}\}<\/span>/g,
      "$1"
    )
    .replace(/<span[^>]*data-variable="[^"]*"[^>]*>\{\{\w+\}\}<\/span>/g, "")
    .replace(/\{\{[^}]+\}\}/g, "");
}

function sortSlides(slides: any[]): any[] {
  const isExternal = (s: any) =>
    s.origin === "sending_client" || s.origin === "external_provider";
  const byNumber = (a: any, b: any) =>
    parseInt(a.number, 10) - parseInt(b.number, 10);

  const regular = slides
    .filter((s) => !isExternal(s) && !s.isAdditional)
    .sort(byNumber);
  const additional = slides
    .filter((s) => !isExternal(s) && s.isAdditional)
    .sort(byNumber);
  const external = slides.filter(isExternal).sort(byNumber);
  return [...regular, ...additional, ...external];
}

function SpecimenCard({
  specimen,
  expanded,
  onToggle,
  onPrintCassettes,
  onPrintSlides,
  showTechnicalPrintActions,
  caseId,
  caseNumber,
  isFinalized,
  isCaseReadOnly,
  allowPostSignOffEvaluation,
  onEditCpt,
}: {
  specimen: any;
  expanded: boolean;
  onToggle: () => void;
  onPrintCassettes: () => void;
  onPrintSlides: () => void;
  showTechnicalPrintActions: boolean;
  caseId: string;
  caseNumber?: string;
  isFinalized?: boolean;
  isCaseReadOnly?: boolean;
  allowPostSignOffEvaluation?: boolean;
  onEditCpt: (info: {
    caseDiagnosisId: string;
    specimenName: string;
    currentCptCode: any;
  }) => void;
}) {
  const blocks = specimen.blocks || [];
  const grossHtml = resolveGrossVariables(specimen.gross || "");
  const hasGross = grossHtml && !isEmptyHtml(grossHtml);

  const primaryDiagnosis =
    (specimen.caseDiagnoses || []).find((d: any) => d.isPrimary) ||
    (specimen.caseDiagnoses || [])[0];
  const diagnosisHtml = primaryDiagnosis?.diagnosisText || "";
  const hasDiagnosis = diagnosisHtml && !isEmptyHtml(diagnosisHtml);
  const microscopicHtml = primaryDiagnosis?.microscopicDescription || "";
  const hasMicroscopic = microscopicHtml && !isEmptyHtml(microscopicHtml);

  const icdCode = primaryDiagnosis?.icdCode;
  const cptCode = primaryDiagnosis?.cptCode;
  const codesLine = [
    icdCode?.code && `ICD-10: ${icdCode.code}`,
    cptCode?.code && `CPT: ${cptCode.displayCode || cptCode.code}`,
  ]
    .filter(Boolean)
    .join(" | ");

  const materialLabel = getMaterialLabel(specimen.material);
  const collectionMethod = specimen.collectionMethod;
  const subsite = specimen.subsite?.name;
  const findings = specimen.findings;
  const fragmentCount = specimen.fragmentCount;
  const fragmentCountDisplay = getFragmentCountDisplayValue(
    fragmentCount,
    specimen.fragmentCountQualifier
  );
  const comments = specimen.comments;
  const shortMeta = [
    materialLabel && { label: "Material", value: materialLabel },
    collectionMethod && {
      label: "Collection",
      value: collectionMethod,
    },
    fragmentCountDisplay && {
      label: "Fragments",
      value: fragmentCountDisplay,
    },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <Box
      style={{
        background: "white",
        borderRadius: 12,
        border: "1px solid var(--mantine-color-gray-2)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      {/* Specimen Header */}
      <UnstyledButton onClick={onToggle} w="100%" style={{ display: "block" }}>
        <Group
          justify="space-between"
          p="md"
          wrap="nowrap"
          style={{
            borderBottom: expanded
              ? "1px solid var(--mantine-color-gray-1)"
              : "none",
            background: "rgba(248,249,250,0.3)",
          }}
        >
          <Group gap="md" wrap="nowrap">
            <Box
              style={{
                background: "#0b50da",
                color: "white",
                fontWeight: 700,
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 8,
                fontSize: 14,
                flexShrink: 0,
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }}
            >
              {specimen.number}
            </Box>
            <Box>
              <Text fw={700} size="md">
                {specimen.source?.name || "Specimen"}
                {subsite && (
                  <Text span size="sm" c="dimmed" fw={400}>
                    {`, ${subsite}`}
                  </Text>
                )}
              </Text>
              <Group gap="xs" wrap="nowrap" align="center">
                {icdCode?.code && (
                  <Text size="xs" c="dimmed">
                    ICD-10: {icdCode.code}
                  </Text>
                )}
                {icdCode?.code && cptCode?.code && (
                  <Text size="xs" c="dimmed">
                    |
                  </Text>
                )}
                {cptCode?.code && (
                  <Group gap={4} wrap="nowrap" align="center">
                    <Text size="xs" c="dimmed">
                      CPT: {cptCode.displayCode || cptCode.code}
                    </Text>
                    {isFinalized && (
                      <UnstyledButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditCpt({
                            caseDiagnosisId: primaryDiagnosis.id,
                            specimenName: specimen.source?.name || "Specimen",
                            currentCptCode: cptCode,
                          });
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: "var(--mantine-color-blue-6)",
                          cursor: "pointer",
                        }}
                      >
                        <IconEdit size={12} style={{ marginLeft: 2 }} />
                      </UnstyledButton>
                    )}
                  </Group>
                )}
              </Group>
            </Box>
          </Group>
          <Group gap="xs" wrap="nowrap">
            {expanded ? (
              <IconChevronUp size={18} color="var(--mantine-color-gray-5)" />
            ) : (
              <IconChevronDown size={18} color="var(--mantine-color-gray-5)" />
            )}
          </Group>
        </Group>
      </UnstyledButton>

      {expanded && (
        <Box
          p="lg"
          style={{ display: "flex", flexDirection: "column", gap: 20 }}
        >
          {/* Metadata row */}
          {(shortMeta.length > 0 || findings) && (
            <Group gap="xl" align="flex-start">
              {shortMeta.map((m) => (
                <Box key={m.label}>
                  <Text size="xs" c="dimmed" fw={500}>
                    {m.label}
                  </Text>
                  <Text size="sm">{m.value}</Text>
                </Box>
              ))}
              {findings && (
                <Box style={{ minWidth: 180, maxWidth: 320 }}>
                  <Text size="xs" c="dimmed" fw={500}>
                    Findings
                  </Text>
                  <Text size="sm" style={{ lineHeight: 1.5 }}>
                    {findings}
                  </Text>
                </Box>
              )}
            </Group>
          )}

          {/* Comments */}
          {comments && (
            <Box>
              <Text size="xs" c="dimmed" fw={500}>
                Comments
              </Text>
              <Text size="sm" style={{ lineHeight: 1.5 }}>
                {comments}
              </Text>
            </Box>
          )}

          {/* Gross Description + Diagnosis + Microscopic side by side */}
          {(hasGross || hasDiagnosis || hasMicroscopic) && (
            <Box
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 24,
              }}
            >
              {hasGross && (
                <Box style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={6}>
                    Gross Description
                  </Text>
                  <Box
                    p="sm"
                    style={{
                      background: "var(--mantine-color-gray-0)",
                      borderRadius: 8,
                      border: "1px solid var(--mantine-color-gray-1)",
                      fontSize: 14,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                    dangerouslySetInnerHTML={{ __html: grossHtml }}
                  />
                </Box>
              )}
              {hasDiagnosis && (
                <Box style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
                  <Group gap={8} mb={6} align="center">
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                      Diagnosis
                    </Text>
                    {primaryDiagnosis?.category?.name && (
                      <Badge
                        size="xs"
                        variant="light"
                        color="violet"
                        radius="sm"
                      >
                        {primaryDiagnosis.category.name}
                      </Badge>
                    )}
                  </Group>
                  <Box
                    p="sm"
                    style={{
                      background: "var(--mantine-color-gray-0)",
                      borderRadius: 8,
                      border: "1px solid var(--mantine-color-gray-1)",
                      fontSize: 14,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                    dangerouslySetInnerHTML={{ __html: diagnosisHtml }}
                  />
                  {codesLine && (
                    <Text size="xs" c="dimmed" mt={6}>
                      {codesLine}
                    </Text>
                  )}
                </Box>
              )}
              {hasMicroscopic && (
                <Box style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={6}>
                    Microscopic Description
                  </Text>
                  <Box
                    p="sm"
                    style={{
                      background: "var(--mantine-color-gray-0)",
                      borderRadius: 8,
                      border: "1px solid var(--mantine-color-gray-1)",
                      fontSize: 14,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                    dangerouslySetInnerHTML={{ __html: microscopicHtml }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Blocks & Slides -- exact Stitch HTML match */}
          {blocks.length > 0 && (
            <Box
              p="md"
              style={{
                background: "var(--mantine-color-gray-0)",
                borderRadius: 8,
              }}
            >
              {/* Title row: "BLOCKS & SLIDES" left, "Print Cassette" + "Print Slides" right */}
              <Group justify="space-between" mb="sm">
                <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                  Blocks &amp; Slides
                </Text>
                {showTechnicalPrintActions && (
                  <Group gap="md">
                    <UnstyledButton
                      onClick={onPrintCassettes}
                      style={{ display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <IconLabel
                        size={14}
                        color="var(--mantine-color-blue-6)"
                      />
                      <Text size="xs" fw={700} c="blue">
                        Print Cassette
                      </Text>
                    </UnstyledButton>
                    <UnstyledButton
                      onClick={onPrintSlides}
                      style={{ display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <IconStack2
                        size={14}
                        color="var(--mantine-color-blue-6)"
                      />
                      <Text size="xs" fw={700} c="blue">
                        Print Slides
                      </Text>
                    </UnstyledButton>
                  </Group>
                )}
              </Group>

              {/* Block rows */}
              <Stack gap={8}>
                {blocks.map((block: any) => {
                  const slides = sortSlides(block.slides || []);
                  const allSlidesCut =
                    slides.length > 0 &&
                    slides.every((s: any) => s.printedAt || s.isCompleted);
                  const blockLabel = `${specimen.number}${block.number}`;
                  return (
                    <Box
                      key={block.id}
                      px="sm"
                      py={8}
                      style={{
                        background: "white",
                        borderRadius: 4,
                        border: "1px solid var(--mantine-color-gray-3)",
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                      }}
                    >
                      {/* Block number */}
                      <Text
                        fw={700}
                        size="sm"
                        c="dimmed"
                        w={40}
                        ta="center"
                        style={{ flexShrink: 0 }}
                      >
                        {blockLabel}
                      </Text>

                      {block.embeddedAt && (
                        <Tooltip label="Embedded" withArrow position="top">
                          <IconLayersIntersect
                            size={16}
                            color="var(--mantine-color-teal-5)"
                            style={{ flexShrink: 0 }}
                          />
                        </Tooltip>
                      )}

                      {allSlidesCut && (
                        <Tooltip
                          label="All slides have been cut"
                          withArrow
                          position="top"
                        >
                          <IconScissors
                            size={16}
                            color="var(--mantine-color-blue-5)"
                            style={{ flexShrink: 0 }}
                          />
                        </Tooltip>
                      )}

                      {block.isCompleted && (
                        <Group gap={4} wrap="nowrap">
                          <ReceivedExternalBadge
                            compact
                            isReprinted={!!block.lastStickerPrintedAt}
                            lastStickerPrintedAt={block.lastStickerPrintedAt}
                          />
                          <PrintStickerButton
                            caseId={caseId}
                            itemId={block.id}
                            itemType="block"
                            isCompleted={block.isCompleted}
                          />
                        </Group>
                      )}

                      {/* Slide pills */}
                      <Box
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          flex: 1,
                        }}
                      >
                        {slides.map((slide: any) => {
                          const slideLabel = `${blockLabel}-${getPhysicalSlideNumber(
                            slide
                          )}`;
                          return (
                            <SlidePill
                              key={slide.id}
                              slide={slide}
                              label={slideLabel}
                              caseId={caseId}
                              caseNumber={caseNumber}
                              isReadOnly={
                                isCaseReadOnly || !!specimen.isSignedOff
                              }
                              allowPostSignOffEvaluation={
                                allowPostSignOffEvaluation
                              }
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function SpecimensSectionV2({
  caseId,
  specimens,
  showTechnicalPrintActions,
  onPrintAllCassettes,
  onPrintAllSlides,
  onPrintSpecimenCassettes,
  onPrintSpecimenSlides,
  printCassetteLoading,
  printSlideLoading,
  isFinalized,
  isBlocked,
  caseNumber,
}: Props) {
  // Same rule the pathologist review screen uses for its read-only state, so a
  // signed-off case behaves identically on both screens.
  const isCaseReadOnly = useMemo(
    () => !!isBlocked || !!isFinalized,
    [isBlocked, isFinalized]
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [cptModalOpened, setCptModalOpened] = useState(false);
  const [cptEditData, setCptEditData] = useState<{
    caseDiagnosisId: string;
    specimenName: string;
    currentCptCode: any;
  } | null>(null);

  useEffect(() => {
    if (specimens.length > 0 && expandedIds.size === 0) {
      setExpandedIds(new Set([specimens[0].id]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specimens]);

  const handleEditCpt = useCallback(
    (info: {
      caseDiagnosisId: string;
      specimenName: string;
      currentCptCode: any;
    }) => {
      setCptEditData(info);
      setCptModalOpened(true);
    },
    []
  );

  const handleCptSuccess = useCallback(() => {
    // Success callback
  }, []);

  const handleCloseCptModal = useCallback(() => {
    setCptModalOpened(false);
    setCptEditData(null);
  }, []);

  const toggleSpecimen = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text
          size="lg"
          fw={700}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <IconCategory size={20} color="#0b50da" />
          Specimens &amp; Blocks
        </Text>
        {showTechnicalPrintActions && (
          <Group gap={8}>
            <Button
              variant="default"
              size="xs"
              fw={700}
              leftSection={<IconPrinter size={14} />}
              onClick={onPrintAllCassettes}
              loading={printCassetteLoading}
            >
              Print All Cassettes
            </Button>
            <Button
              variant="default"
              size="xs"
              fw={700}
              leftSection={<IconPrinter size={14} />}
              onClick={onPrintAllSlides}
              loading={printSlideLoading}
            >
              Print All Slides
            </Button>
          </Group>
        )}
      </Group>

      {specimens.length === 0 ? (
        <Box
          p="xl"
          style={{
            background: "white",
            borderRadius: 12,
            border: "1px solid var(--mantine-color-gray-2)",
            textAlign: "center",
          }}
        >
          <Text size="sm" c="dimmed">
            No specimens have been added to this case yet.
          </Text>
        </Box>
      ) : (
        <Stack gap="md">
          {specimens.map((s) => (
            <SpecimenCard
              key={s.id}
              specimen={s}
              expanded={expandedIds.has(s.id)}
              onToggle={() => toggleSpecimen(s.id)}
              onPrintCassettes={() => onPrintSpecimenCassettes(s.id)}
              onPrintSlides={() => onPrintSpecimenSlides(s.id)}
              showTechnicalPrintActions={showTechnicalPrintActions}
              caseId={caseId}
              caseNumber={caseNumber}
              isCaseReadOnly={isCaseReadOnly}
              isFinalized={isFinalized}
              allowPostSignOffEvaluation={!isBlocked}
              onEditCpt={handleEditCpt}
            />
          ))}
        </Stack>
      )}

      {cptModalOpened && cptEditData && (
        <CptCorrectionModal
          opened={cptModalOpened}
          onClose={handleCloseCptModal}
          caseId={caseId}
          caseNumber={caseNumber || ""}
          caseDiagnosisId={cptEditData.caseDiagnosisId}
          specimenName={cptEditData.specimenName}
          currentCptCode={cptEditData.currentCptCode}
          onSuccess={handleCptSuccess}
        />
      )}
    </Box>
  );
}
